import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '../../shared/api/apiClient';
import { RefreshIcon } from '../../shared/icons';
import type {
    CreateShopBuyerRequest,
    CreateShopCompanyRequest,
    ShopBuyerDto,
    ShopCompanyDetailsDto,
    ShopCompanyDto,
    ShopStaffUserDto,
    UpdateShopBuyerRequest,
    UpdateShopCompanyRequest
} from '../../types';
import '../UserManagement/styles.css';
import './styles.css';

const parseTriState = (value: string): boolean | null => {
    if (value === 'true') return true;
    if (value === 'false') return false;
    return null;
};

const triStateValue = (value: boolean | null | undefined): string => {
    if (value === true) return 'true';
    if (value === false) return 'false';
    return '';
};

const PriceTriStateSelect: React.FC<{
    value: string;
    disabled?: boolean;
    onChange: (value: string) => void;
}> = ({ value, disabled, onChange }) => (
    <select
        className="form-input-custom form-select-custom"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
    >
        <option value="">Как у компании</option>
        <option value="true">Показать</option>
        <option value="false">Скрыть</option>
    </select>
);

const parseOptionalUserId = (value: string): number | null => {
    if (!value) return null;
    const parsed = Number(value);
    return Number.isInteger(parsed) ? parsed : null;
};

const StaffManagerSelect: React.FC<{
    value: string;
    staffUsers: ShopStaffUserDto[];
    disabled?: boolean;
    allowEmpty?: boolean;
    emptyLabel?: string;
    onChange: (value: string) => void;
}> = ({ value, staffUsers, disabled, allowEmpty, emptyLabel, onChange }) => (
    <select
        className="form-input-custom form-select-custom"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
    >
        {allowEmpty && <option value="">{emptyLabel || 'Как у компании'}</option>}
        {staffUsers.map(user => (
            <option key={user.id} value={user.id}>
                {user.userName}{user.email ? ` (${user.email})` : ''}
            </option>
        ))}
    </select>
);

const CompanyModal: React.FC<{
    isOpen: boolean;
    staffUsers: ShopStaffUserDto[];
    onClose: () => void;
    onSave: (data: CreateShopCompanyRequest) => Promise<void>;
}> = ({ isOpen, staffUsers, onClose, onSave }) => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [managerUserId, setManagerUserId] = useState('');
    const [showPrice1, setShowPrice1] = useState(true);
    const [showPrice2, setShowPrice2] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        setName('');
        setDescription('');
        setManagerUserId(staffUsers[0]?.id || '');
        setShowPrice1(true);
        setShowPrice2(false);
        setLoading(false);
    }, [isOpen, staffUsers]);

    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };
        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
        }
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const handleSave = async () => {
        const managerId = parseOptionalUserId(managerUserId);
        if (!name.trim() || managerId == null) return;

        setLoading(true);
        try {
            await onSave({
                name: name.trim(),
                description: description.trim() || undefined,
                defaultManagerUserId: managerId,
                showPrice1,
                showPrice2
            });
            onClose();
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal-backdrop-custom" onClick={(event) => event.target === event.currentTarget && onClose()}>
            <div className="modal-dialog-custom animate-in">
                <div className="modal-content-custom">
                    <div className="modal-header-custom">
                        <div>
                            <h3 className="modal-title-custom">Создание компании</h3>
                            <p className="modal-subtitle-custom">Компания кабинета покупателя</p>
                        </div>
                        <button type="button" className="btn-close-custom" onClick={onClose} disabled={loading}>
                            <i className="bi bi-x-lg"></i>
                        </button>
                    </div>
                    <div className="modal-body-custom">
                        <div className="form-section">
                            <div className="form-grid">
                                <div className="form-group-custom">
                                    <label className="form-label-custom">Название</label>
                                    <input
                                        type="text"
                                        className="form-input-custom"
                                        value={name}
                                        onChange={(event) => setName(event.target.value)}
                                        disabled={loading}
                                        maxLength={200}
                                    />
                                </div>
                                <div className="form-group-custom">
                                    <label className="form-label-custom">Менеджер по закупкам</label>
                                    <StaffManagerSelect
                                        value={managerUserId}
                                        staffUsers={staffUsers}
                                        disabled={loading}
                                        onChange={setManagerUserId}
                                    />
                                </div>
                                <div className="form-group-custom shop-span-2">
                                    <label className="form-label-custom">Описание</label>
                                    <textarea
                                        className="form-input-custom"
                                        value={description}
                                        onChange={(event) => setDescription(event.target.value)}
                                        disabled={loading}
                                        maxLength={1000}
                                        rows={4}
                                    />
                                </div>
                                <div className="form-group-custom shop-span-2">
                                    <label className="form-label-custom shop-checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={showPrice1}
                                            onChange={(event) => setShowPrice1(event.target.checked)}
                                            disabled={loading}
                                        />
                                        Цена 1 (ц1)
                                    </label>
                                    <label className="form-label-custom shop-checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={showPrice2}
                                            onChange={(event) => setShowPrice2(event.target.checked)}
                                            disabled={loading}
                                        />
                                        Цена 2 (ц2)
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer-custom">
                        <button type="button" className="btn-secondary-custom" onClick={onClose} disabled={loading}>
                            Отмена
                        </button>
                        <button
                            type="button"
                            className="btn-primary-custom"
                            onClick={handleSave}
                            disabled={loading || !name.trim() || !managerUserId || (!showPrice1 && !showPrice2)}
                        >
                            {loading ? <span className="btn-spinner"></span> : <i className="bi bi-check-lg"></i>}
                            Создать
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const BuyerModal: React.FC<{
    isOpen: boolean;
    mode: 'create' | 'edit';
    buyer?: ShopBuyerDto;
    staffUsers: ShopStaffUserDto[];
    onClose: () => void;
    onSave: (data: CreateShopBuyerRequest | UpdateShopBuyerRequest) => Promise<void>;
}> = ({ isOpen, mode, buyer, staffUsers, onClose, onSave }) => {
    const [userName, setUserName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [managerUserId, setManagerUserId] = useState('');
    const [showPrice1, setShowPrice1] = useState('');
    const [showPrice2, setShowPrice2] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        setUserName(buyer?.userName || '');
        setEmail(buyer?.email || '');
        setPassword('');
        setManagerUserId(buyer?.managerUserId != null ? String(buyer.managerUserId) : '');
        setShowPrice1(triStateValue(buyer?.showPrice1));
        setShowPrice2(triStateValue(buyer?.showPrice2));
        setLoading(false);
    }, [isOpen, buyer]);

    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };
        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
        }
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const isCreate = mode === 'create';

    const handleSave = async () => {
        if (!userName.trim() || !email.trim()) return;
        if (isCreate && password.trim().length < 8) return;

        setLoading(true);
        try {
            if (isCreate) {
                await onSave({
                    userName: userName.trim(),
                    email: email.trim(),
                    password: password.trim(),
                    managerUserId: parseOptionalUserId(managerUserId),
                    showPrice1: parseTriState(showPrice1),
                    showPrice2: parseTriState(showPrice2)
                } as CreateShopBuyerRequest);
            } else {
                await onSave({
                    userName: userName.trim(),
                    email: email.trim(),
                    managerUserId: parseOptionalUserId(managerUserId),
                    showPrice1: parseTriState(showPrice1),
                    showPrice2: parseTriState(showPrice2)
                } as UpdateShopBuyerRequest);
            }
            onClose();
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal-backdrop-custom" onClick={(event) => event.target === event.currentTarget && onClose()}>
            <div className="modal-dialog-custom animate-in">
                <div className="modal-content-custom">
                    <div className="modal-header-custom">
                        <div>
                            <h3 className="modal-title-custom">{isCreate ? 'Новый покупатель' : 'Редактирование покупателя'}</h3>
                            <p className="modal-subtitle-custom">Доступ в кабинет компании</p>
                        </div>
                        <button type="button" className="btn-close-custom" onClick={onClose} disabled={loading}>
                            <i className="bi bi-x-lg"></i>
                        </button>
                    </div>
                    <div className="modal-body-custom">
                        <div className="form-section">
                            <div className="form-grid">
                                <div className="form-group-custom">
                                    <label className="form-label-custom">Логин</label>
                                    <input
                                        type="text"
                                        className="form-input-custom"
                                        value={userName}
                                        onChange={(event) => setUserName(event.target.value)}
                                        disabled={loading}
                                    />
                                </div>
                                <div className="form-group-custom">
                                    <label className="form-label-custom">Email</label>
                                    <input
                                        type="email"
                                        className="form-input-custom"
                                        value={email}
                                        onChange={(event) => setEmail(event.target.value)}
                                        disabled={loading}
                                    />
                                </div>
                                {isCreate && (
                                    <div className="form-group-custom">
                                        <label className="form-label-custom">Пароль</label>
                                        <input
                                            type="password"
                                            className="form-input-custom"
                                            value={password}
                                            onChange={(event) => setPassword(event.target.value)}
                                            disabled={loading}
                                            minLength={8}
                                        />
                                        <span className="form-hint">Минимум 8 символов</span>
                                    </div>
                                )}
                                <div className="form-group-custom">
                                    <label className="form-label-custom">Менеджер по закупкам</label>
                                    <StaffManagerSelect
                                        value={managerUserId}
                                        staffUsers={staffUsers}
                                        disabled={loading}
                                        allowEmpty
                                        emptyLabel="Как у компании"
                                        onChange={setManagerUserId}
                                    />
                                </div>
                                <div className="form-group-custom">
                                    <label className="form-label-custom">Цена 1 (ц1)</label>
                                    <PriceTriStateSelect
                                        value={showPrice1}
                                        disabled={loading}
                                        onChange={setShowPrice1}
                                    />
                                </div>
                                <div className="form-group-custom">
                                    <label className="form-label-custom">Цена 2 (ц2)</label>
                                    <PriceTriStateSelect
                                        value={showPrice2}
                                        disabled={loading}
                                        onChange={setShowPrice2}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer-custom">
                        <button type="button" className="btn-secondary-custom" onClick={onClose} disabled={loading}>
                            Отмена
                        </button>
                        <button
                            type="button"
                            className="btn-primary-custom"
                            onClick={handleSave}
                            disabled={loading || !userName.trim() || !email.trim() || (isCreate && password.trim().length < 8)}
                        >
                            {loading ? <span className="btn-spinner"></span> : <i className="bi bi-check-lg"></i>}
                            {isCreate ? 'Создать' : 'Сохранить'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const PasswordModal: React.FC<{
    isOpen: boolean;
    buyer?: ShopBuyerDto;
    onClose: () => void;
    onSave: (password: string) => Promise<void>;
}> = ({ isOpen, buyer, onClose, onSave }) => {
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setPassword('');
            setLoading(false);
        }
    }, [isOpen]);

    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };
        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
        }
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    if (!isOpen || !buyer) return null;

    const handleSave = async () => {
        if (password.trim().length < 8) return;
        setLoading(true);
        try {
            await onSave(password.trim());
            onClose();
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal-backdrop-custom" onClick={(event) => event.target === event.currentTarget && onClose()}>
            <div className="modal-dialog-custom animate-in">
                <div className="modal-content-custom">
                    <div className="modal-header-custom">
                        <div>
                            <h3 className="modal-title-custom">Сброс пароля</h3>
                            <p className="modal-subtitle-custom">{buyer.userName}</p>
                        </div>
                        <button type="button" className="btn-close-custom" onClick={onClose} disabled={loading}>
                            <i className="bi bi-x-lg"></i>
                        </button>
                    </div>
                    <div className="modal-body-custom">
                        <div className="form-group-custom">
                            <label className="form-label-custom">Новый пароль</label>
                            <input
                                type="password"
                                className="form-input-custom"
                                value={password}
                                onChange={(event) => setPassword(event.target.value)}
                                disabled={loading}
                                minLength={8}
                            />
                            <span className="form-hint">Минимум 8 символов</span>
                        </div>
                    </div>
                    <div className="modal-footer-custom">
                        <button type="button" className="btn-secondary-custom" onClick={onClose} disabled={loading}>
                            Отмена
                        </button>
                        <button
                            type="button"
                            className="btn-primary-custom"
                            onClick={handleSave}
                            disabled={loading || password.trim().length < 8}
                        >
                            {loading ? <span className="btn-spinner"></span> : <i className="bi bi-check-lg"></i>}
                            Сохранить
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const CompanyDetail: React.FC<{
    companyId: number;
    staffUsers: ShopStaffUserDto[];
    onBack: () => void;
}> = ({ companyId, staffUsers, onBack }) => {
    const [company, setCompany] = useState<ShopCompanyDetailsDto | null>(null);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [managerUserId, setManagerUserId] = useState('');
    const [isActive, setIsActive] = useState(true);
    const [showPrice1, setShowPrice1] = useState(true);
    const [showPrice2, setShowPrice2] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [buyerModalMode, setBuyerModalMode] = useState<'create' | 'edit' | null>(null);
    const [selectedBuyer, setSelectedBuyer] = useState<ShopBuyerDto | null>(null);
    const [passwordBuyer, setPasswordBuyer] = useState<ShopBuyerDto | null>(null);

    const loadCompany = React.useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const result = await api.get<ShopCompanyDetailsDto>(`/api/admin/companies/${companyId}`);
            const details = result.data;
            setCompany(details);
            setName(details.name);
            setDescription(details.description || '');
            setManagerUserId(String(details.defaultManagerUserId));
            setIsActive(details.isActive);
            setShowPrice1(details.showPrice1);
            setShowPrice2(details.showPrice2);
        } catch (err) {
            setError('Не удалось загрузить компанию');
            console.error('Error loading company:', err);
        } finally {
            setLoading(false);
        }
    }, [companyId]);

    useEffect(() => {
        loadCompany();
    }, [loadCompany]);

    const handleSaveCompany = async () => {
        const managerId = parseOptionalUserId(managerUserId);
        if (!name.trim() || managerId == null) return;

        const payload: UpdateShopCompanyRequest = {
            name: name.trim(),
            description: description.trim() || undefined,
            defaultManagerUserId: managerId,
            isActive,
            showPrice1,
            showPrice2
        };

        setSaving(true);
        try {
            const result = await api.put<ShopCompanyDetailsDto>(`/api/admin/companies/${companyId}`, payload);
            setCompany(result.data);
            toast.success('Компания обновлена');
        } finally {
            setSaving(false);
        }
    };

    const handleSaveBuyer = async (data: CreateShopBuyerRequest | UpdateShopBuyerRequest) => {
        if (buyerModalMode === 'create') {
            await api.post<ShopBuyerDto>(`/api/admin/companies/${companyId}/buyers`, data);
            toast.success('Покупатель создан');
        } else if (selectedBuyer) {
            await api.put<ShopBuyerDto>(
                `/api/admin/companies/${companyId}/buyers/${selectedBuyer.userId}`,
                data);
            toast.success('Покупатель обновлён');
        }
        await loadCompany();
    };

    const handleSetPassword = async (password: string) => {
        if (!passwordBuyer) return;
        await api.post(`/api/admin/companies/${companyId}/buyers/${passwordBuyer.userId}/password`, { password });
        toast.success('Пароль обновлён');
    };

    if (error) {
        return (
            <div className="alert alert-danger">
                <h5 className="alert-heading">Ошибка</h5>
                <p>{error}</p>
                <button className="btn btn-primary" onClick={loadCompany}>Повторить загрузку</button>
                <button className="btn btn-secondary ml-2" onClick={onBack}>К списку</button>
            </div>
        );
    }

    if (loading && !company) {
        return (
            <div className="text-center py-4">
                <span className="btn-spinner"></span>
            </div>
        );
    }

    return (
        <div className="user-management container-fluid">
            <div className="filter-controls-compact">
                <div className="filter-controls-row">
                    <div className="shop-detail-heading">
                        <button className="btn-compact" onClick={onBack} title="К списку">
                            <i className="bi bi-arrow-left"></i>
                            <span>К списку</span>
                        </button>
                        <h3>{company?.name || 'Компания'}</h3>
                    </div>
                    <div className="filter-controls-right">
                        <button className="btn-compact refresh" onClick={loadCompany} disabled={loading} title="Обновить">
                            {loading ? <span className="btn-spinner"></span> : <RefreshIcon />}
                        </button>
                    </div>
                </div>
            </div>

            <div className="shop-panel">
                <h4>Компания</h4>
                <div className="form-grid">
                    <div className="form-group-custom">
                        <label className="form-label-custom">Название</label>
                        <input
                            type="text"
                            className="form-input-custom"
                            value={name}
                            onChange={(event) => setName(event.target.value)}
                            maxLength={200}
                            disabled={saving}
                        />
                    </div>
                    <div className="form-group-custom">
                        <label className="form-label-custom">Менеджер по закупкам</label>
                        <StaffManagerSelect
                            value={managerUserId}
                            staffUsers={staffUsers}
                            disabled={saving}
                            onChange={setManagerUserId}
                        />
                    </div>
                    <div className="form-group-custom shop-span-2">
                        <label className="form-label-custom">Описание</label>
                        <textarea
                            className="form-input-custom"
                            value={description}
                            onChange={(event) => setDescription(event.target.value)}
                            maxLength={1000}
                            rows={3}
                            disabled={saving}
                        />
                    </div>
                    <div className="form-group-custom">
                        <label className="form-label-custom shop-checkbox-label">
                            <input
                                type="checkbox"
                                checked={isActive}
                                onChange={(event) => setIsActive(event.target.checked)}
                                disabled={saving}
                            />
                            Активна
                        </label>
                    </div>
                    <div className="form-group-custom shop-span-2">
                        <label className="form-label-custom shop-checkbox-label">
                            <input
                                type="checkbox"
                                checked={showPrice1}
                                onChange={(event) => setShowPrice1(event.target.checked)}
                                disabled={saving}
                            />
                            Цена 1 (ц1)
                        </label>
                        <label className="form-label-custom shop-checkbox-label">
                            <input
                                type="checkbox"
                                checked={showPrice2}
                                onChange={(event) => setShowPrice2(event.target.checked)}
                                disabled={saving}
                            />
                            Цена 2 (ц2)
                        </label>
                    </div>
                </div>
                <div className="shop-panel-actions">
                    <button
                        type="button"
                        className="btn-primary-custom"
                        onClick={handleSaveCompany}
                        disabled={saving || !name.trim() || !managerUserId || (!showPrice1 && !showPrice2)}
                    >
                        {saving ? <span className="btn-spinner"></span> : <i className="bi bi-check-lg"></i>}
                        Сохранить
                    </button>
                </div>
            </div>

            <div className="shop-panel">
                <div className="shop-panel-header">
                    <h4>Покупатели</h4>
                    <button
                        className="btn-compact primary"
                        onClick={() => {
                            setSelectedBuyer(null);
                            setBuyerModalMode('create');
                        }}
                    >
                        <i className="bi bi-plus-lg"></i>
                        <span>Добавить</span>
                    </button>
                </div>
                <div className="user-management-table-container">
                    <table className="user-management-table">
                        <thead>
                            <tr>
                                <th>Логин</th>
                                <th>Email</th>
                                <th>Менеджер по закупкам</th>
                                <th className="actions-column"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {(company?.buyers || []).map(buyer => (
                                <tr key={buyer.userId}>
                                    <td><strong>{buyer.userName}</strong></td>
                                    <td>{buyer.email}</td>
                                    <td>{buyer.managerUserName || 'Как у компании'}</td>
                                    <td className="actions-column">
                                        <div className="btn-group btn-group-sm">
                                            <button
                                                className="btn btn-small btn-edit"
                                                title="Редактировать"
                                                onClick={() => {
                                                    setSelectedBuyer(buyer);
                                                    setBuyerModalMode('edit');
                                                }}
                                            >
                                                <i className="bi bi-pencil"></i>
                                            </button>
                                            <button
                                                className="btn btn-small btn-edit"
                                                title="Сбросить пароль"
                                                onClick={() => setPasswordBuyer(buyer)}
                                            >
                                                <i className="bi bi-key"></i>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {(company?.buyers || []).length === 0 && (
                    <div className="text-center py-4">
                        <p className="text-muted">Покупатели не найдены</p>
                    </div>
                )}
            </div>

            <BuyerModal
                isOpen={buyerModalMode !== null}
                mode={buyerModalMode || 'create'}
                buyer={selectedBuyer || undefined}
                staffUsers={staffUsers}
                onClose={() => {
                    setBuyerModalMode(null);
                    setSelectedBuyer(null);
                }}
                onSave={handleSaveBuyer}
            />

            <PasswordModal
                isOpen={passwordBuyer !== null}
                buyer={passwordBuyer || undefined}
                onClose={() => setPasswordBuyer(null)}
                onSave={handleSetPassword}
            />
        </div>
    );
};

const ShopCompanyManagement: React.FC = () => {
    const [companies, setCompanies] = useState<ShopCompanyDto[]>([]);
    const [staffUsers, setStaffUsers] = useState<ShopStaffUserDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [createOpen, setCreateOpen] = useState(false);
    const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);

    const loadCompanies = async () => {
        try {
            setLoading(true);
            setError(null);
            const result = await api.get<{ companies: ShopCompanyDto[] }>('/api/admin/companies');
            setCompanies(result.data.companies || []);
        } catch (err) {
            setError('Произошла ошибка при загрузке компаний');
            console.error('Error loading companies:', err);
        } finally {
            setLoading(false);
        }
    };

    const loadStaffUsers = async () => {
        const result = await api.get<{ users: ShopStaffUserDto[] }>('/api/admin/companies/staff');
        setStaffUsers(result.data.users || []);
    };

    useEffect(() => {
        loadCompanies();
        loadStaffUsers().catch((err) => {
            console.error('Error loading staff users:', err);
        });
    }, []);

    const filteredCompanies = useMemo(() => {
        const normalizedSearch = searchTerm.toLowerCase();
        return companies.filter(company =>
            !normalizedSearch
            || company.name.toLowerCase().includes(normalizedSearch)
            || (company.description || '').toLowerCase().includes(normalizedSearch)
            || company.defaultManagerUserName.toLowerCase().includes(normalizedSearch));
    }, [companies, searchTerm]);

    const handleCreate = async (data: CreateShopCompanyRequest) => {
        const result = await api.post<ShopCompanyDetailsDto>('/api/admin/companies', data);
        toast.success('Компания создана');
        await loadCompanies();
        setSelectedCompanyId(result.data.id);
    };

    if (selectedCompanyId != null) {
        return (
            <CompanyDetail
                companyId={selectedCompanyId}
                staffUsers={staffUsers}
                onBack={() => {
                    setSelectedCompanyId(null);
                    loadCompanies();
                }}
            />
        );
    }

    if (error) {
        return (
            <div className="alert alert-danger">
                <h5 className="alert-heading">Ошибка</h5>
                <p>{error}</p>
                <button className="btn btn-primary" onClick={loadCompanies}>Повторить загрузку</button>
            </div>
        );
    }

    return (
        <div className="user-management container-fluid">
            <div className="filter-controls-compact">
                <div className="filter-controls-row">
                    <h3>Компании</h3>
                    <div className="filter-controls-right">
                        <div className="search-input-compact">
                            <i className="bi bi-search search-icon"></i>
                            <input
                                type="text"
                                className="form-control-compact"
                                placeholder="Поиск..."
                                value={searchTerm}
                                onChange={(event) => setSearchTerm(event.target.value)}
                            />
                            {searchTerm && (
                                <button className="clear-search" onClick={() => setSearchTerm('')} title="Очистить поиск">
                                    <i className="bi bi-x"></i>
                                </button>
                            )}
                        </div>
                        <button className="btn-compact refresh" onClick={loadCompanies} disabled={loading} title="Обновить">
                            {loading ? <span className="btn-spinner"></span> : <RefreshIcon />}
                        </button>
                        <button className="btn-compact primary" onClick={() => setCreateOpen(true)}>
                            <i className="bi bi-plus-lg"></i>
                            <span>Создать</span>
                        </button>
                    </div>
                </div>
            </div>

            <div className="user-management-table-container">
                <table className="user-management-table">
                    <thead>
                        <tr>
                            <th>Название</th>
                            <th>Менеджер по закупкам</th>
                            <th>Статус</th>
                            <th className="actions-column"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredCompanies.map(company => (
                            <tr
                                key={company.id}
                                className="shop-clickable-row"
                                onClick={() => setSelectedCompanyId(company.id)}
                            >
                                <td>
                                    <strong>{company.name}</strong>
                                    {company.description && <div className="text-muted small">{company.description}</div>}
                                </td>
                                <td>{company.defaultManagerUserName}</td>
                                <td>
                                    <span className={`badge ${company.isActive ? 'bg-success' : 'bg-secondary'}`}>
                                        {company.isActive ? 'Активна' : 'Отключена'}
                                    </span>
                                </td>
                                <td className="actions-column">
                                    <div className="btn-group btn-group-sm">
                                        <button
                                            className="btn btn-small btn-edit"
                                            title="Открыть"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                setSelectedCompanyId(company.id);
                                            }}
                                        >
                                            <i className="bi bi-box-arrow-in-right"></i>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {filteredCompanies.length === 0 && (
                <div className="text-center py-4">
                    <p className="text-muted">Компании не найдены</p>
                </div>
            )}

            <div className="mt-3 text-muted">
                Показано {filteredCompanies.length} из {companies.length} компаний
            </div>

            <CompanyModal
                isOpen={createOpen}
                staffUsers={staffUsers}
                onClose={() => setCreateOpen(false)}
                onSave={handleCreate}
            />
        </div>
    );
};

export default ShopCompanyManagement;
